/**
 * What people did on a case.
 *
 * `EarlierDecisionsCard` lists decisions made on lines the current draft no longer has
 * (the mail was evaluated again and the line text changed). `HistoryCard` is the log of
 * the case: who took it, confirmed a line, set a status. It is collapsed by default
 * and reads the log only when it is opened.
 */

import { Card, Collapsible, Skeleton, Timeline, Typography } from "@norican/react";
import { useState } from "react";

import { type LineDecision, personName, useCaseEvents } from "@/lib/api";
import { decisionSentence, ORPHAN_DECISION_WORDS } from "@/lib/work-labels";

function formatDateTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function decisionItemText(decision: LineDecision): string {
  if (decision.kind === "no_item") {
    return "no item";
  }
  return [decision.item, decision.item_name].filter(Boolean).join(" ") || "no item";
}

export function EarlierDecisionsCard({ decisions }: { decisions: LineDecision[] }) {
  if (decisions.length === 0) {
    return null;
  }
  return (
    <Card variant="outline" padding="none">
      <Card.Header padding="sm" className="border-border-subtle border-b">
        <Typography variant="label">Earlier decisions</Typography>
      </Card.Header>
      <Card.Body className="flex flex-col gap-2">
        <Typography variant="caption" tone="subtle">
          {ORPHAN_DECISION_WORDS.caption}
        </Typography>
        <ul className="flex flex-col gap-2">
          {decisions.map((decision) => (
            <li
              key={decision.line_key}
              className="flex min-w-0 flex-col text-sm"
              data-orphan-decision=""
            >
              <span className="text-fg-subtle text-xs font-semibold">
                {ORPHAN_DECISION_WORDS.label}
              </span>
              {decision.enquiry_line && (
                <span className="text-fg break-words">{decision.enquiry_line}</span>
              )}
              <span className="text-fg-muted text-xs break-words">
                {decisionSentence(decision, personName)}: {decisionItemText(decision)}
              </span>
            </li>
          ))}
        </ul>
      </Card.Body>
    </Card>
  );
}

export function HistoryCard({ caseId }: { caseId: string }) {
  const [open, setOpen] = useState(false);
  const events = useCaseEvents(caseId, open);
  const items = events.data ?? [];

  return (
    <Card variant="outline" padding="none">
      <Collapsible.Root open={open} onOpenChange={setOpen}>
        <Card.Header padding="sm" className="flex items-center gap-2">
          <Typography variant="label">History</Typography>
          <Collapsible.Trigger className="text-accent-fg-subtle ml-auto text-xs font-semibold hover:underline">
            {open ? "Hide" : "Show who did what"}
          </Collapsible.Trigger>
        </Card.Header>
        <Collapsible.Panel>
          <div className="border-border-subtle border-t px-4 py-3">
            {events.isLoading ? (
              <Skeleton variant="rect" height={48} />
            ) : events.isError ? (
              <Typography variant="body-sm" tone="muted">
                I2Q could not load the history. Close and open it to try again.
              </Typography>
            ) : items.length === 0 ? (
              <Typography variant="body-sm" tone="muted">
                Nobody has worked on this case yet.
              </Typography>
            ) : (
              <Timeline
                items={items.map((event, index) => ({
                  id: `${event.at}-${index}`,
                  title: event.text || `${personName(event.actor)}: ${event.action}`,
                  timestamp: formatDateTime(event.at),
                }))}
              />
            )}
          </div>
        </Collapsible.Panel>
      </Collapsible.Root>
    </Card>
  );
}
